import React, { useMemo, useState } from "react";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import PageHeader from "pages/components/PageHeader";
import ProjectTable from "pages/components/ProjectTable/ProjectTable";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";

const formatData = (v) => (v ? String(v).substring(0, 10) : "");

const formatValor = (v) =>
  Number(v || 0).toLocaleString("pt-PT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const labelOrigem = (v) => {
  switch (String(v || "")) {
    case "CAMPANHAS":
      return "Campanhas";
    case "INCENTIVOS":
      return "Incentivos";
    case "DESCONTOS_CLIENTE":
      return "Descontos Cliente";
    case "INDEFINIDO":
      return "Indefinido";
    default:
      return v || "";
  }
};

const labelTipo = (v) => {
  switch (String(v || "")) {
    case "APURAMENTO":
      return "Apuramento";
    case "RECEBIMENTO":
      return "Recebimento";
    case "AJUSTE":
      return "Ajuste";
    default:
      return v || "";
  }
};

export default function FabricantesContaCorrente() {
  const { showAlert } = useAlert();
  const { temFuncao, userData } = useAuth();

  const isAdminLike =
    temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");

  const [loading, setLoading] = useState(false);

  const [fabricantes, setFabricantes] = useState([]);
  const [fabricanteInput, setFabricanteInput] = useState("");
  const [fabricanteSelecionado, setFabricanteSelecionado] = useState(null);

  const [form, setForm] = useState({
    datainicial: "",
    datafinal: "",
  });

  const [movimentos, setMovimentos] = useState([]);

  const totais = useMemo(() => {
    const apurado = movimentos
      .filter((x) => String(x.tipo_movimento || "") === "APURAMENTO")
      .reduce((acc, x) => acc + Number(x.valor || 0), 0);

    const recebido = movimentos
      .filter((x) => String(x.tipo_movimento || "") === "RECEBIMENTO")
      .reduce((acc, x) => acc + Math.abs(Number(x.valor || 0)), 0);

    const saldo = movimentos.reduce((acc, x) => acc + Number(x.valor || 0), 0);

    return { apurado, recebido, saldo };
  }, [movimentos]);

  const movimentosPorOrigem = useMemo(() => {
    const origens = ["CAMPANHAS", "INCENTIVOS", "DESCONTOS_CLIENTE", "INDEFINIDO"];

    return origens
      .map((origem) => {
        const linhas = movimentos.filter((x) => String(x.origem || "") === origem);
        const total = linhas.reduce((acc, x) => acc + Number(x.valor || 0), 0);

        return {
          origem,
          label: labelOrigem(origem),
          linhas,
          total,
        };
      })
      .filter((x) => x.origem !== "INDEFINIDO" || x.linhas.length > 0);
  }, [movimentos]);

  const columns = useMemo(
    () => [
      {
        Header: "Data",
        accessor: "data_movimento",
        width: 110,
        Cell: ({ value }) => formatData(value),
      },
      {
        Header: "Tipo",
        accessor: "tipo_movimento",
        width: 150,
        Cell: ({ value }) => labelTipo(value),
      },
      {
        Header: "Descrição",
        accessor: "descricao",
        width: 420,
        sortType: "textPt",
      },
      {
        Header: "Valor",
        accessor: "valor",
        width: 130,
        sortType: "numPt",
        Cell: ({ value }) => <span style={{ fontWeight: 700 }}>{formatValor(value)}</span>,
      },
      {
        Header: "Documento",
        accessor: "refdocumento",
        width: 160,
        sortType: "textPt",
      },
      {
        Header: "Obs.",
        accessor: "obs",
        width: 260,
        sortType: "textPt",
      },
    ],
    []
  );

  const carregarFabricantes = async (term = "") => {
    const [payload, err] = await handleApi(
      api.post("FabricantesContaCorrente/SelFabricantes", { term })
    );

    if (err) {
      showAlert(err, "error");
      return;
    }

    setFabricantes(Array.isArray(payload) ? payload : []);
  };

  const consultar = async () => {
    if (!isAdminLike) {
      showAlert("Sem permissões para aceder a esta página.", "error");
      return;
    }

    if (!fabricanteSelecionado?.ref || !form.datainicial || !form.datafinal) {
      showAlert("Seleciona fabricante, data inicial e data final.", "warning");
      return;
    }

    setLoading(true);

    try {
      const [, syncErr] = await handleApi(
        api.post("FabricantesContaCorrente/SyncContaCorrenteCampanhas", {
          reffabricante: fabricanteSelecionado.ref,
          datainicial: form.datainicial,
          datafinal: form.datafinal,
        })
      );

      if (syncErr) {
        showAlert(syncErr, "error");
        return;
      }

      const [, syncIncentivosErr] = await handleApi(
        api.post("Comercial/SyncContaCorrenteIncentivos", {
          reffabricante: fabricanteSelecionado.ref,
          datainicial: form.datainicial,
          datafinal: form.datafinal,
          idutilizador: userData?.id || null,
        })
      );

      if (syncIncentivosErr) {
        showAlert(syncIncentivosErr, "error");
        return;
      }

      const [dados, err] = await handleApi(
        api.post("FabricantesContaCorrente/SelContaCorrente", {
          reffabricante: fabricanteSelecionado.ref,
          datainicial: form.datainicial,
          datafinal: form.datafinal,
        })
      );

      if (err) {
        showAlert(err, "error");
        return;
      }

      setMovimentos(Array.isArray(dados) ? dados : []);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {loading && (
        <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </MDBox>
      )}

      <PageHeader />

      <MDBox pt={1}>
        <Card>
          <MDBox p={{ xs: 1.5, md: 3 }}>
            <MDTypography variant="h6" mb={2}>
              Conta corrente de fabricantes
            </MDTypography>

            <MDBox
              display="grid"
              gap={1.5}
              sx={{
                gridTemplateColumns: { xs: "1fr", md: "2fr 1fr 1fr auto" },
                alignItems: "end",
              }}
            >
              <div>
                <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>Fabricante</div>
                <Autocomplete
                  size="small"
                  options={fabricantes}
                  filterOptions={(x) => x}
                  inputValue={fabricanteInput}
                  value={fabricanteSelecionado}
                  getOptionLabel={(option) =>
                    option?.nome ? `${option.nome} (${option.ref})` : ""
                  }
                  onInputChange={(_, value, reason) => {
                    if (reason === "input") {
                      setFabricanteInput(value);
                      carregarFabricantes(value);
                    }

                    if (reason === "clear") {
                      setFabricanteInput("");
                      setFabricanteSelecionado(null);
                    }
                  }}
                  onOpen={() => carregarFabricantes(fabricanteInput)}
                  onChange={(_, value) => {
                    setFabricanteSelecionado(value);
                    setFabricanteInput(value?.nome ? `${value.nome} (${value.ref})` : "");
                  }}
                  renderInput={(params) => <TextField {...params} placeholder="Selecione..." />}
                />
              </div>

              <MDInput
                label="Data inicial"
                type="date"
                value={form.datainicial}
                onChange={(e) => setForm((p) => ({ ...p, datainicial: e.target.value }))}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />

              <MDInput
                label="Data final"
                type="date"
                value={form.datafinal}
                onChange={(e) => setForm((p) => ({ ...p, datafinal: e.target.value }))}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />

              <MDButton color="info" onClick={consultar} disabled={!isAdminLike}>
                Consultar
              </MDButton>
            </MDBox>

            <MDBox
              mt={3}
              display="grid"
              gap={1.5}
              sx={{ gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" } }}
            >
              <Card variant="outlined">
                <MDBox p={2}>
                  <MDTypography variant="caption" color="text">
                    Apurado
                  </MDTypography>
                  <MDTypography variant="h5">{formatValor(totais.apurado)} €</MDTypography>
                </MDBox>
              </Card>

              <Card variant="outlined">
                <MDBox p={2}>
                  <MDTypography variant="caption" color="text">
                    Recebido
                  </MDTypography>
                  <MDTypography variant="h5">{formatValor(totais.recebido)} €</MDTypography>
                </MDBox>
              </Card>

              <Card variant="outlined">
                <MDBox p={2}>
                  <MDTypography variant="caption" color="text">
                    Saldo
                  </MDTypography>
                  <MDTypography variant="h5">{formatValor(totais.saldo)} €</MDTypography>
                </MDBox>
              </Card>
            </MDBox>

            {movimentosPorOrigem.map((grupo) => (
              <MDBox key={grupo.origem} mt={3}>
                <ProjectTable
                  tableKey={`fabricantes-conta-corrente-${grupo.origem.toLowerCase()}`}
                  version="1"
                  title={`${grupo.label} (${formatValor(grupo.total)} €)`}
                  subtitle={`${grupo.linhas.length} movimento(s)`}
                  columns={columns}
                  data={grupo.linhas}
                  loading={loading}
                  emptyMessage="Sem movimentos."
                  minWidth={1350}
                  maxHeight="45vh"
                  defaultSortBy={[{ id: "valor", desc: true }]}
                  showKeepFilters
                />
              </MDBox>
            ))}
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}
